import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Headset, Bot, User as UserIcon } from "lucide-react";
import client, { API } from "@/lib/api";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
const SENDER_META = {
  customer: { label: "You", cls: "bg-orange text-white ml-auto", icon: UserIcon },
  agent: { label: "Agent", cls: "bg-graphite text-white", icon: Headset },
  bot: { label: "Assistant", cls: "bg-surface text-graphite border border-border", icon: Bot },
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem("gp_chat")) || null; } catch { return null; } });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [agentOnline, setAgentOnline] = useState(false);
  const [starting, setStarting] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const seen = useRef(new Set());

  const merge = (incoming) => {
    setMessages((prev) => {
      const map = new Map(prev.map((m) => [m.id, m]));
      incoming.forEach((m) => map.set(m.id, m));
      incoming.forEach((m) => seen.current.add(m.id));
      return Array.from(map.values()).sort((a, b) => (a.at < b.at ? -1 : 1));
    });
  };

  const connectWs = (sid) => {
    try {
      const ws = new WebSocket(`${WS_BASE}/api/ws/chat/${sid}`);
      ws.onmessage = (e) => { try { const d = JSON.parse(e.data); if (d.type === "message") merge([d]); } catch {} };
      ws.onclose = () => { wsRef.current = null; };
      wsRef.current = ws;
    } catch { wsRef.current = null; }
  };

  // hydrate existing session on mount
  useEffect(() => {
    if (session?.id) {
      client.get(`/chat/${session.id}/messages`).then((r) => { merge(r.data.messages); setAgentOnline(r.data.agent_online); });
      connectWs(session.id);
    }
    return () => { wsRef.current && wsRef.current.close(); };
  }, []);

  // safety poll while open
  useEffect(() => {
    if (!open || !session?.id) return;
    const t = setInterval(() => {
      client.get(`/chat/${session.id}/messages`).then((r) => { merge(r.data.messages); setAgentOnline(r.data.agent_online); }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [open, session]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const start = async (e) => {
    e.preventDefault();
    setStarting(true);
    try {
      const { data } = await client.post("/chat/start", { email, name });
      const s = { id: data.session.id, email: data.session.email, name: data.session.name };
      localStorage.setItem("gp_chat", JSON.stringify(s));
      setSession(s);
      merge(data.messages);
      setAgentOnline(data.agent_online);
      connectWs(s.id);
    } finally { setStarting(false); }
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !session?.id) return;
    setText("");
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ text: t }));
    } else {
      await client.post(`/chat/${session.id}/message`, { text: t });
      client.get(`/chat/${session.id}/messages`).then((r) => merge(r.data.messages));
    }
  };

  return (
    <>
      <button data-testid="chat-toggle" onClick={() => setOpen((o) => !o)} className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-orange hover:bg-orange-hover text-white grid place-items-center shadow-lg transition-transform hover:scale-105">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-[380px] h-[68vh] max-h-[560px] bg-white border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden gp-fade-up">
          <div className="bg-graphite text-white px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange grid place-items-center"><Headset className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-semibold text-sm">GOT PARTS Support</div>
              <div className="text-xs text-white/60 flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${agentOnline ? "bg-emerald-400" : "bg-amber-400"}`} />{agentOnline ? "Agent online" : "Assistant (bot)"}</div>
            </div>
          </div>

          {!session ? (
            <form onSubmit={start} className="flex-1 p-5 flex flex-col justify-center gap-3" data-testid="chat-start-form">
              <p className="text-sm text-steel">Enter your email to start. We'll keep your conversation if you come back.</p>
              <div><label className="text-xs text-steel-light">Email *</label><input data-testid="chat-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-11 px-3 border border-border rounded-md outline-none focus:border-orange" placeholder="you@company.com" /></div>
              <div><label className="text-xs text-steel-light">Name</label><input data-testid="chat-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-11 px-3 border border-border rounded-md outline-none focus:border-orange" placeholder="Optional" /></div>
              <button data-testid="chat-start-submit" disabled={starting} className="h-11 bg-orange hover:bg-orange-hover text-white rounded-md font-medium">{starting ? "Starting…" : "Start chat"}</button>
            </form>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto thin-scroll p-3 space-y-2 bg-surface/40">
                <div className="text-center text-xs text-steel-light py-2">{agentOnline ? "An agent will reply shortly." : "Our assistant can help with parts, orders and quotes."}</div>
                {messages.map((m) => {
                  const meta = SENDER_META[m.sender] || SENDER_META.bot;
                  return (
                    <div key={m.id} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${meta.cls}`}>
                      {m.sender !== "customer" && <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{meta.label}</div>}
                      <div className="whitespace-pre-wrap leading-snug">{m.text}</div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-2.5 border-t border-border flex items-center gap-2">
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 h-10 px-3 border border-border rounded-md outline-none focus:border-orange text-sm" />
                <button data-testid="chat-send" onClick={send} className="w-10 h-10 grid place-items-center bg-orange hover:bg-orange-hover text-white rounded-md"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
